import { useEffect } from "react";
import { useLocation } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";

// Generate or retrieve a visitor ID from localStorage
const getVisitorId = () => {
  let visitorId = localStorage.getItem("visitor_id");
  if (!visitorId) {
    visitorId = `visitor_${Date.now()}_${Math.random()
      .toString(36)
      .substr(2, 9)}`;
    localStorage.setItem("visitor_id", visitorId);
  }
  return visitorId;
};

// Track page visit to the database
const trackPageVisit = async (pathname: string) => {
  try {
    const visitorId = getVisitorId();
    const {
      data: { user },
    } = await supabase.auth.getUser();

    // Get basic info
    const pageTitle = document.title;
    const referrer = document.referrer;
    const userAgent = navigator.userAgent;

    // Track the visit
    await supabase.from("site_visits").insert({
      visitor_id: visitorId,
      user_id: user?.id || null,
      page_path: pathname,
      page_title: pageTitle,
      referrer: referrer || null,
      user_agent: userAgent,
      visited_at: new Date().toISOString(),
    });
  } catch (error) {
    // Silently fail - don't disrupt user experience if tracking fails
    console.debug("Page tracking error:", error);
  }
};

/**
 * Custom hook to track page visits
 * Use this hook in pages or in the main App component
 */
export const usePageTracking = () => {
  const location = useLocation();

  useEffect(() => {
    // Track the current page
    trackPageVisit(location.pathname);

    // Track session start time for duration calculation
    const sessionStart = Date.now();

    // Cleanup: Track session duration on page leave (optional)
    return () => {
      const sessionDuration = Math.floor((Date.now() - sessionStart) / 1000);
      // You could update the visit record with session duration here if needed
      console.debug(
        `Session duration on ${location.pathname}: ${sessionDuration}s`
      );
    };
  }, [location.pathname]);
};

export default usePageTracking;
